import { SetStateAction, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Grid, Card, CardContent,
  Tabs, Tab, InputBase, IconButton, Chip, useTheme, alpha,
  FormControl, Select, MenuItem, Button, CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  MenuOpen as MenuOpenIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

import Sidebar from '../components/common/Sidebar';
import * as articleApi from '../services/article';
import { Article, Innovation } from '../services/article';

const drawerWidth = 240;

const MainContent = styled(Box)<{ isSidebarOpen: boolean }>(({ theme, isSidebarOpen }) => ({
  flexGrow: 1,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isSidebarOpen ? drawerWidth : 0,
  width: '100%'
}));

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  border: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '50ch',
    },
  },
}));

const MotionCard = motion(Card);

const ArticlesListPage = () => {
  const navigate = useNavigate();

  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('All');
  const [newsArticles, setNewsArticles] = useState<Article[]>([]);
  const [innovationArticles, setInnovationArticles] = useState<Innovation[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  // Fetch articles from API
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const [articlesData, innovationsData] = await Promise.all([
          articleApi.getArticles(),
          articleApi.getInnovations()
        ]);
        setNewsArticles(articlesData);
        setInnovationArticles(innovationsData);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  // Combine and get unique industries
  const allArticles = [...newsArticles, ...innovationArticles];
  const industries = ['All', ...Array.from(new Set(allArticles.map(article => article.industry)))];

  const handleTabChange = (_event: any, newValue: SetStateAction<number>) => {
    setTabValue(newValue);
  };

  const handleSearchChange = (event: { target: { value: SetStateAction<string>; }; }) => {
    setSearchTerm(event.target.value);
  };

  const handleIndustryChange = (event: { target: { value: SetStateAction<string>; }; }) => {
    setIndustryFilter(event.target.value);
  };

  const handleArticleClick = (type: string, id: string) => {
    navigate(`/article/${type}/${id}`);
  };

  // Filter articles based on search term, tab value, and industry filter
  let filteredArticles: (Article | Innovation)[] = [];

  if (tabValue === 0) {
    // All articles
    filteredArticles = allArticles;
  } else if (tabValue === 1) {
    // News articles
    filteredArticles = newsArticles;
  } else {
    // Innovation articles
    filteredArticles = innovationArticles;
  }

  // Apply search filter
  if (searchTerm) {
    filteredArticles = filteredArticles.filter(article =>
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.summary.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  // Apply industry filter
  if (industryFilter !== 'All') {
    filteredArticles = filteredArticles.filter(article => article.industry === industryFilter);
  }

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      
      
      <MainContent isSidebarOpen={false}>
       
        <Container maxWidth="xl" sx={{ pt: 6, pb: 8 }}>
          {/* Page Header */}
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.secondary.main, 0.8)})`,
              position: 'relative',
              overflow: 'hidden',
              color: 'white',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '50%',
                height: '100%',
                opacity: 0.1,
                background: `radial-gradient(circle, ${theme.palette.common.white} 0%, transparent 70%)`,
                transform: 'translateX(30%)'
              }}
            />
            
            <Box 
              sx={{
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Typography 
                variant="h3" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 700,
                  mb: 1
                }}
              >
                Articles & Insights
              </Typography>
              
              <Typography 
                variant="h6"
                sx={{ 
                  opacity: 0.9,
                  mb: 2,
                  fontWeight: 400
                }}
              >
                Latest news, innovations, and updates from Saudi Arabia's industrial and investment landscape
              </Typography>
            </Box>
          </Paper>

          {/* Filters and Tabs */}
          <Paper
            elevation={1}
            sx={{
              borderRadius: 3,
              mb: 4,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: 2
              }}
            >
              <Search>
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
                <StyledInputBase
                  placeholder="Search articles…"
                  inputProps={{ 'aria-label': 'search' }}
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </Search>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                  <FilterIcon sx={{ mr: 1, fontSize: 20 }} />
                  Filter by Industry:
                </Typography>
                
                <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                  <Select
                    value={industryFilter}
                    onChange={handleIndustryChange}
                    sx={{ borderRadius: 2 }}
                  >
                    {industries.map((industry, index) => (
                      <MenuItem key={index} value={industry}>{industry}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                px: 2,
                '& .MuiTab-root': {
                  minHeight: 60,
                  px: 3
                }
              }}
            >
              <Tab 
                label="All Articles" 
                sx={{ 
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              />
              <Tab 
                label="News" 
                sx={{ 
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              />
              <Tab 
                label="Innovation Spotlight" 
                sx={{ 
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              />
            </Tabs>
          </Paper>

          {/* Results Count */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Showing {filteredArticles.length} articles
              {industryFilter !== 'All' && ` in ${industryFilter}`}
              {searchTerm && ` matching "${searchTerm}"`}
            </Typography>
            
            <Button 
              variant="text" 
              onClick={() => {
                setSearchTerm('');
                setIndustryFilter('All');
              }}
              disabled={!searchTerm && industryFilter === 'All'}
            >
              Clear Filters
            </Button>
          </Box>

          {/* Articles Grid */}
          {filteredArticles.length > 0 ? (
            <Grid container spacing={3}>
              {filteredArticles.map((article) => {
                const articleType = newsArticles.some(a => a.id === article.id) ? 'news' : 'innovation';
                const cardColor = articleType === 'news'
                  ? { main: theme.palette.primary.main, light: theme.palette.primary.light }
                  : { main: '#F9A825', light: '#FDD835' };

                return (
                  <Grid item xs={12} md={6} lg={4} key={`${articleType}-${article.id}`}>
                    <MotionCard
                      whileHover={{ y: -8 }}
                      elevation={2}
                      sx={{
                        borderRadius: 2,
                        height: '100%',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 12px 20px -10px rgba(0,0,0,0.2)'
                        },
                        cursor: 'pointer',
                        overflow: 'hidden'
                      }}
                      onClick={() => handleArticleClick(articleType, article.id)}
                    >
                      <Box
                        sx={{
                          height: 8,
                          width: '100%',
                          background: `linear-gradient(90deg, ${cardColor.main}, ${cardColor.light} 50%, transparent)`
                        }}
                      />

                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Chip
                            label={articleType === 'news' ? 'News' : 'Innovation'}
                            size="small"
                            sx={{
                              borderRadius: 1,
                              bgcolor: articleType === 'news' ? theme.palette.primary.main : '#F9A825',
                              color: 'white'
                            }}
                          />
                          
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                            <CalendarIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            {article.date}
                          </Typography>
                        </Box>
                        
                        <Typography 
                          variant="h5"
                          sx={{ 
                            mb: 2,
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            lineHeight: 1.3
                          }}
                        >
                          {article.title}
                        </Typography>
                        
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ mb: 3 }}
                        >
                          {article.summary}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <BusinessIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {article.industry}
                          </Typography>
                        </Box>
                        
                        {article.tags && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {article.tags.slice(0, 3).map((tag, index) => (
                              <Chip 
                                key={index} 
                                label={tag} 
                                size="small" 
                                variant="outlined"
                                sx={{ borderRadius: 1, fontSize: '0.7rem' }}
                              />
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </MotionCard>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`
              }}
            >
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 500 }}>
                No articles found
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Try adjusting your search or filter criteria to find what you're looking for.
              </Typography>
            </Paper>
          )}
        </Container>
      </MainContent>
    </Box>
  );
};

export default ArticlesListPage;